import { motion } from 'framer-motion';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { NavMenu } from './nav-menu';
import { LanguageSelector } from './language-selector';
import { Button } from '@/components/ui/button';
import { Download, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePlatform } from '@/hooks/use-platform';
import logo from '@/assets/logos/coffeeback-logo-gpt.png';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

export function LandingHeader() {
    const { header } = useScrollAnimation();
    const { t } = useTranslation();
    const { platform } = usePlatform();
    const [isScrolled, setIsScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogoClick = () => {
        if (location.pathname !== '/') {
            navigate('/');
            // Small delay to allow the new page to mount before scrolling
            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleDownload = () => {
        if (platform === 'ios') {
            window.open('https://apps.apple.com', '_blank');
        } else {
            window.open('https://play.google.com', '_blank');
        }
    };

    return (
        <motion.header
            style={{
                backgroundColor: header.background,
                backdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'none',
                WebkitBackdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'none',
                borderBottom: isScrolled ? '1px solid rgba(111, 78, 55, 0.08)' : 'none',
                boxShadow: isScrolled ? '0 4px 6px rgba(45, 24, 16, 0.07)' : 'none',
            }}
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300
                ${isScrolled ? 'h-14' : 'h-16'}`}
        >
            <div className="container h-full mx-auto px-4 flex items-center justify-between">
                {/* ─── Logo ─── */}
                <div
                    className="flex items-center gap-2 sm:gap-3 cursor-pointer group shrink-0"
                    onClick={handleLogoClick}
                >
                    <img
                        src={logo}
                        alt="Coffeeback"
                        className={`w-auto transition-all duration-300 group-hover:scale-105
                            ${isScrolled ? 'h-8 sm:h-9' : 'h-10 sm:h-12'}`}
                    />
                    <span className={`font-display font-bold italic transition-all duration-300
                        group-hover:text-[#6F4E37]
                        ${isScrolled ? 'text-base sm:text-lg' : 'text-lg sm:text-2xl'}`}
                    >
                        Coffeeback
                    </span>
                </div>

                {/* ─── Navigation + Controls ─── */}
                <div className="flex items-center gap-2">
                    <NavMenu isScrolled={isScrolled} />

                    {/* Desktop: Language */}
                    <div className="hidden xl:flex items-center pl-3 ml-1 border-l border-foreground/10">
                        <LanguageSelector />
                    </div>

                    {/* Desktop: Download icon + Auth CTAs */}
                    <div className="hidden xl:flex items-center gap-2 ml-2">
                        {/* Download icon-only (glass-pill style) */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleDownload}
                            className="rounded-full w-8 h-8 bg-background/50 backdrop-blur-sm border border-border/50 hover:bg-background/80"
                            aria-label={t('header.cta.download')}
                        >
                            <Download className="h-4 w-4" />
                        </Button>

                        {/* Criar Conta — secondary outline */}
                        <Link to="/register">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-[#6F4E37] border border-[#6F4E37]/20
                                    hover:bg-[#6F4E37]/10 hover:border-[#6F4E37]/40
                                    bg-white/50 backdrop-blur-sm
                                    transition-all duration-300 rounded-full"
                            >
                                {t('header.cta.register')}
                            </Button>
                        </Link>

                        {/* Entrar — primary filled */}
                        <Link to="/login">
                            <Button
                                size="sm"
                                className="text-white border-0 rounded-full  shadow-md hover:shadow-lg
                                    transition-all duration-300 hover:scale-[1.02]"
                                style={{
                                    background: 'linear-gradient(135deg, #6F4E37 0%, #A67C52 100%)',
                                }}
                            >
                                {t('header.cta.login')}
                            </Button>
                        </Link>
                    </div>

                    {/* Mobile: Login CTA (Always visible) */}
                    <div className="flex xl:hidden items-center gap-2">
                        <Link to="/login">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="rounded-full w-9 h-9 border border-[#6F4E37]/15 bg-white/50 backdrop-blur-sm text-[#6F4E37] hover:bg-[#6F4E37]/10 shadow-sm"
                                aria-label={t('header.cta.login')}
                            >
                                <User className="h-4 w-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </motion.header>
    );
}
